import React, { Children } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import styles from './tag-section.module.css'

const propTypes = {
    className: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]).isRequired,
    onRemove: PropTypes.func,
    tagIdentity: PropTypes.string,
    titleClassName: PropTypes.string,
    titleLeft: PropTypes.string,
    titleRight: PropTypes.string
}

const defaultProps = {
    tagIdentity: 'id'
}

/**
 * Renders a tag container with tags and a title consisting of two parts (left and right)
 * @example
 * <TagSection id='1' titleLeft='Cities:' titleRight='visited' onRemove={(itemId, sectionId) => {}}>
 *     <Tag id='1'>Berlin</Tag>
 *     <Tag id='2'>Barcelona</Tag>
 * </TagSection>
 *
 * @param {Object} props - The component properties
 * @param {number|string} props.id - Unique identifier for this tag section
 * @param {string} [props.tagIdentity='id'] - The property name within child-components for unique id
 * @param {string} [props.titleLeft] - Title string to render on the left side
 * @param {string} [props.titleRight] - Title string to render on the right side
 * @param {string} [props.className] - CSS class to add to the styling of the element
 * @param {string} [props.titleClassName] - CSS class to add to the styling of the titles
 * @param {Function} [props.onRemove] - Callback to call when the close icon on any tab is clicked
 */
function TagSection(props) {
    const { children, className, id, onRemove, tagIdentity, titleClassName, titleLeft, titleRight } = props

    const createCallback = tag => (onRemove ? () => onRemove(tag.props[tagIdentity], id) : null)

    const tags = Children.toArray(children).map(tag =>
        React.cloneElement(tag, {
            onClose: createCallback(tag)
        })
    )

    const titles = (
        <div className={cx(styles['titles'], titleClassName)}>
            <span className={styles['title-left']}>{titleLeft}</span>
            <span className={styles['title-right']}>{titleRight}</span>
        </div>
    )

    return (
        <div className={cx(styles['section'], className)}>
            {titles}
            {tags}
        </div>
    )
}

TagSection.propTypes = propTypes
TagSection.defaultProps = defaultProps

export default TagSection
